import { isAfter, parseISO, startOfDay } from 'date-fns'
import {
  RESIDENT_COMPANY_ROLES,
  RESIDENT_LIBERTY_ROLES,
  RESIDENT_POLICIES,
  RESIDENT_PROPERTY_ROLES,
} from '../constants/residentConstants'
import type {
  RentalListResponse,
  ResidentDefault,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '../types'

export const RESIDENT_DEFAULT_REQUIRED_FIELDS: Array<keyof ResidentDefaultFormValues> = [
  'reason_for_default',
  'covid_19',
  'first_month_not_paid_month',
  'first_month_not_paid_year',
]

const REIMBURSEMENT_RENTAL_STATUSES = new Set(['SKIPPED', 'EVICTION'])
const SAVE_BUTTON_TABS = new Set<string>(['residentinfo', 'default', 'calculation'])

export const REIMBURSEMENT_REQUEST_DATE_AFTER_LEASE_END_MESSAGE =
  'Reimbursement Request Date can not exceed Lease End Date'

function parseCalendarDate(value: unknown): Date | null {
  if (value === undefined || value === null || value === '') return null
  const raw = String(value).trim()
  if (!raw) return null
  const iso = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw
  try {
    const parsed = parseISO(iso.includes('T') ? iso : `${iso}T12:00:00`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  } catch {
    return null
  }
}

/** Angular `updateDuplicateValue` when `control_name === 'rental_status_date'` (day-level compare). */
export function isReimbursementRequestDateAfterLeaseEnd(
  rentalStatus: string,
  rentalStatusDate: string,
  leaseEndDate: string,
): boolean {
  if (!REIMBURSEMENT_RENTAL_STATUSES.has(rentalStatus)) return false
  const requestDate = parseCalendarDate(rentalStatusDate)
  const endDate = parseCalendarDate(leaseEndDate)
  if (!requestDate || !endDate) return false
  return isAfter(startOfDay(requestDate), startOfDay(endDate))
}

/**
 * Mirrors Angular `resident-edit-new` `submitForm` / `isFormValid`:
 * required Reason / COVID / first-month fields apply only on the **Reimbursement**
 * tab (`selectedTab === 'default'`), SKIPPED/EVICTION, non–External Insurance.
 * Calculation-tab save does **not** run this gate (see Angular ~1294).
 */
export function needsDefaultRequiredFields(
  roleName: string,
  selectedTab: string,
  rentalStatus: string,
): boolean {
  return (
    roleName !== 'EXTERNAL_INSURANCE' &&
    selectedTab === 'default' &&
    REIMBURSEMENT_RENTAL_STATUSES.has(rentalStatus)
  )
}

export function getMissingDefaultRequiredFields(
  values: Pick<
    ResidentDefaultFormValues,
    'rental_status' | 'reason_for_default' | 'covid_19' | 'first_month_not_paid_month' | 'first_month_not_paid_year'
  >,
  roleName: string,
  selectedTab: string,
): Array<keyof ResidentDefaultFormValues> {
  if (!needsDefaultRequiredFields(roleName, selectedTab, values.rental_status)) return []
  return RESIDENT_DEFAULT_REQUIRED_FIELDS.filter((field) => {
    const value = values[field as keyof typeof values]
    return value === undefined || value === null || String(value).trim() === ''
  })
}

/**
 * Locked claims are read-only for non-Liberty roles.
 * Mirrors Angular form `[ngClass non-clickable]` when
 * `claim_lock === 'Locked' && !liberty_roleslist.includes(role)`.
 * (Lock *toggle* remains Admin / Liberty Defaults only.)
 */
export function isClaimLockedForRole(roleName: string, claimLock?: string): boolean {
  return claimLock === 'Locked' && !RESIDENT_LIBERTY_ROLES.includes(roleName)
}

/** PMC (company/property) roles must not see the View Application action on Resident / Reimbursement detail. */
export function isPmcRole(roleName: string): boolean {
  return (
    (RESIDENT_COMPANY_ROLES as readonly string[]).includes(roleName) ||
    (RESIDENT_PROPERTY_ROLES as readonly string[]).includes(roleName)
  )
}

/**
 * Angular co-resident name / applicant card → `/application/:id`.
 * Parent strip is `non-clickable` when the role is not in `liberty_roleslist` —
 * the name link itself is not gated by `application_detail_view`.
 */
export function canNavigateApplicantCardToApplication(roleName: string): boolean {
  return RESIDENT_LIBERTY_ROLES.includes(roleName)
}

/**
 * Angular `resident-edit-new` "View Application" button: gated by
 * `application_detail_view` and hidden for PMC roles on resident /
 * reimbursement detail headers.
 */
export function canShowViewApplicationLink(
  roleName: string,
  hasPolicy: (policies: string[]) => boolean,
): boolean {
  if (isPmcRole(roleName)) return false
  return hasPolicy([RESIDENT_POLICIES.applicationDetailView])
}

/**
 * Denied claims are read-only for non-Liberty roles. Mirrors Angular's
 * `disableforClaimDenied` / denied gates for company-property viewers.
 */
export function isClaimDeniedForExternalRole(roleName: string, claimStatus?: string): boolean {
  return claimStatus === 'DENIED' && !RESIDENT_LIBERTY_ROLES.includes(roleName)
}

/**
 * Angular `disableforClaimLock` / `disableforClaimDenied` flags — set only for
 * company/property roles when claim is Locked or DENIED.
 */
export function isPmcClaimFieldLock(
  roleName: string,
  residentDefault?: Pick<ResidentDefault, 'claim_lock' | 'claim_status'> | null,
): boolean {
  if (!isPmcRole(roleName)) return false
  return (
    residentDefault?.claim_lock === 'Locked' || residentDefault?.claim_status === 'DENIED'
  )
}

/**
 * Angular `allow-clickable` on Save / Cancel Reimbursement when
 * `claim_lock === 'Locked' && !liberty_roleslist`.
 */
export function allowsNonLibertyClaimLockActions(
  roleName: string,
  claimLock?: string | null,
): boolean {
  return claimLock === 'Locked' && !RESIDENT_LIBERTY_ROLES.includes(roleName)
}

/**
 * Public notes compose — Angular wraps Notes & Messages with
 * `allow-clickable` when PMC `disableforClaimLock` or `disableforClaimDenied`.
 * Other form/status locks still block compose.
 */
export function isPublicNotesComposeDisabled(
  roleName: string,
  residentDefault?: Pick<ResidentDefault, 'claim_lock' | 'claim_status' | 'rental_status'> | null,
  progressStatus?: string,
  selectedTab?: string,
): boolean {
  if (roleName === 'EXTERNAL_INSURANCE') return true
  if (isPmcClaimFieldLock(roleName, residentDefault)) {
    return progressStatus === 'Policy Activation Pending'
  }
  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(roleName)
  if (
    selectedTab === 'residentinfo' &&
    residentDefault?.rental_status &&
    REIMBURSEMENT_RENTAL_STATUSES.has(residentDefault.rental_status) &&
    !isLibertyRole
  ) {
    return true
  }
  if (progressStatus === 'Policy Activation Pending') return true
  if (residentDefault?.claim_lock === 'Locked' && !isLibertyRole) return true
  if (residentDefault?.claim_status === 'DENIED' && !isLibertyRole) return true
  return false
}

/**
 * Default / resident-info Save. Angular disables only for PMC
 * `disableforClaimDenied`; claim lock uses `allow-clickable` instead.
 */
export function isResidentDetailSaveDisabled(
  roleName: string,
  _formDisabled: boolean,
  residentDefault?: Pick<ResidentDefault, 'claim_lock' | 'claim_status'> | null,
  selectedTab?: string,
): boolean {
  if (roleName === 'EXTERNAL_INSURANCE') return true
  if (isPmcRole(roleName) && residentDefault?.claim_status === 'DENIED') {
    if (!selectedTab || selectedTab === 'calculation') return true
  }
  if (allowsNonLibertyClaimLockActions(roleName, residentDefault?.claim_lock)) return false
  return false
}

/**
 * Angular Final Ledger lock uses `is_final_ledger === '1'` (string).
 * API/form may also send boolean `true` / numeric `1`.
 */
export function isFinalLedgerLockedFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

export type ClaimDocumentLockState = {
  /** Block file input / Upload (Angular file wrap + row locks). */
  uploadsLocked: boolean
  /** Block Remove (Angular row/col locks; not `files_view`). */
  removesLocked: boolean
  /** Payment Ledger upload/remove also locks when Final Ledger is locked. */
  paymentLedgerUploadsLocked: boolean
  paymentLedgerRemovesLocked: boolean
}

/**
 * Angular claim-document upload/remove locks (resident-edit-new.component.html:1113, :1206):
 * PMC `disableforClaimLock`/`disableforClaimDenied`, COMPLETED progress / RECEIVED docs,
 * `resident_default_files_view` on the upload wrap, Final Ledger on the Payment Ledger.
 * Not the form-level lock — Angular scopes that to the Rental Status row (:749).
 *
 * Prefer live form `isFinalLedger` (checkbox) over stale `resident_default.is_final_ledger`
 * so unchecking immediately re-enables upload (then Save persists).
 */
export function getClaimDocumentLockState(args: {
  canUploadFiles: boolean
  roleName: string
  residentDefault?: Pick<
    ResidentDefault,
    'claim_lock' | 'claim_status' | 'rental_progress' | 'document_status' | 'is_final_ledger'
  > | null
  /** Live form checkbox value; falls back to `residentDefault.is_final_ledger`. */
  isFinalLedger?: unknown
}): ClaimDocumentLockState {
  const { canUploadFiles, roleName, residentDefault } = args
  const pmcLock = isPmcClaimFieldLock(roleName, residentDefault)
  const progressOrReceived =
    residentDefault?.rental_progress === 'COMPLETED' ||
    residentDefault?.document_status === 'RECEIVED'
  const rowLocked = pmcLock || progressOrReceived
  const finalLedgerLocked = isFinalLedgerLockedFlag(
    // Always prefer the live form checkbox when the caller passes it (including '').
    // Falling back to resident_default only when the arg is omitted.
    args.isFinalLedger !== undefined ? args.isFinalLedger : residentDefault?.is_final_ledger,
  )
  const received = residentDefault?.document_status === 'RECEIVED'

  return {
    uploadsLocked: rowLocked || !canUploadFiles,
    removesLocked: rowLocked,
    // Angular payment-ledger column: claimLock/denied || is_final_ledger || RECEIVED
    // (COMPLETED comes from the parent lease/ledger row).
    paymentLedgerUploadsLocked: rowLocked || !canUploadFiles || finalLedgerLocked || received,
    paymentLedgerRemovesLocked: rowLocked || finalLedgerLocked || received,
  }
}

/**
 * Angular `reason_for_default` locks — Reason is not gated like Rental Status / Progress:
 * Reimbursement tab (html:786) uses EXTERNAL_INSURANCE / claim lock / denied only, and the
 * Resident Info tab (:449-451) restores it via `allow-clickable` when the claim is unlocked.
 */
export function isReasonFieldLocked(args: {
  roleName: string
  selectedTab?: string
  residentDefault?: Pick<ResidentDefault, 'claim_lock' | 'claim_status'> | null
  progressStatus?: string
}): boolean {
  const { roleName, selectedTab, residentDefault, progressStatus } = args
  if (roleName === 'EXTERNAL_INSURANCE') return true
  if (selectedTab === 'default') {
    return isPmcClaimFieldLock(roleName, residentDefault)
  }
  if (!RESIDENT_LIBERTY_ROLES.includes(roleName)) {
    return residentDefault?.claim_lock === 'Locked'
  }
  return progressStatus === 'Policy Activation Pending'
}

/**
 * Aggregate disable check for the resident edit form. Mirrors Angular's:
 *   - EXTERNAL_INSURANCE -> readonly
 *   - form `[ngClass non-clickable]` when claim_lock=Locked && !liberty role
 *   - Resident Info rental-status row: SKIPPED/EVICTION && !liberty -> non-clickable
 *   - claim_status=DENIED && !liberty -> disable
 *   - Policy Activation Pending -> non-clickable
 */
export function isResidentFormDisabled(
  roleName: string,
  residentDefault?: Pick<ResidentDefault, 'claim_lock' | 'claim_status' | 'rental_status'>,
  progressStatus?: string,
  _selectedTab?: string,
): boolean {
  if (roleName === 'EXTERNAL_INSURANCE') return true
  if (progressStatus === 'Policy Activation Pending') return true

  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(roleName)

  // Angular form wrapper: Locked && !liberty_roleslist → non-clickable (any non-Liberty).
  if (residentDefault?.claim_lock === 'Locked' && !isLibertyRole) {
    return true
  }

  // Angular status row (Resident Info & Reimbursement tabs): SKIPPED/EVICTION && !liberty → non-clickable.
  if (
    residentDefault?.rental_status &&
    REIMBURSEMENT_RENTAL_STATUSES.has(residentDefault.rental_status) &&
    !isLibertyRole
  ) {
    return true
  }

  if (residentDefault?.claim_status === 'DENIED' && !isLibertyRole) {
    return true
  }
  return false
}

/**
 * Angular calculation panel `[ngClass non-clickable]` on Reimbursement Calculation Information:
 * ROLE_LIBERTY_SALES / EXTERNAL_INSURANCE / PMC roles, or ACTIVE|CLOSED with cancel-claim flag.
 * (Calc tab is Liberty-only in React; Sales + External Insurance are the live cases.)
 */
export function isCalculationPanelLocked(
  roleName: string,
  rentalStatus?: string | null,
  isCancelClaim = false,
): boolean {
  const lockedRoles = new Set<string>([
    'ROLE_PROPERTY_MANAGER',
    'ROLE_COMPANY_ADMIN',
    'EXTERNAL_INSURANCE',
    'ROLE_LIBERTY_SALES',
    ...(RESIDENT_COMPANY_ROLES as readonly string[]),
    ...(RESIDENT_PROPERTY_ROLES as readonly string[]),
  ])
  if (lockedRoles.has(roleName)) return true
  if (['ACTIVE', 'CLOSED'].includes(String(rentalStatus || '')) && isCancelClaim) return true
  return false
}

/**
 * Angular roles that may see the Calculation tab *content* panel
 * ("Reimbursement Calculation Information"). Underwriter / Fin Mgr can open the
 * tab but are not in this list — Angular leaves the panel empty for them.
 */
export const CALCULATION_CONTENT_PANEL_ROLES = [
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_ADMIN',
  'EXTERNAL_INSURANCE',
  'ROLE_LIBERTY_SALES',
  'ROLE_EXECS',
  'ROLE_LIBERTY_ACCOUNTING',
] as const

/** Angular `libertyApproved` / `allhasCheckNumber` from payment_history. */
export function resolveCalculationPaymentHistoryFlags(
  paymentHistory: Array<Record<string, unknown>> | null | undefined,
): { libertyApproved: boolean; allhasCheckNumber: boolean } {
  let libertyApproved = false
  let allhasCheckNumber = false
  for (const element of paymentHistory || []) {
    if (String(element?.liberty_approved ?? '') === '1') {
      libertyApproved = true
      if (element?.check_number && String(element?.payment_status ?? '') === 'Completed') {
        allhasCheckNumber = true
      }
    }
  }
  return { libertyApproved, allhasCheckNumber }
}

/**
 * Angular calculation-tab content `*ngIf` (operator precedence preserved):
 * `((roleAllowlist || pmc+approved+check || pmc+COMPLETED) && !NOT_STARTED|ACTIVE|CLOSED)
 *  || ((ACTIVE|CLOSED) && isCancelClaim)`.
 *
 * For Underwriter / Fin Mgr on SKIPPED|EVICTION: tab may show, panel stays empty.
 */
export function canShowCalculationContentPanel(args: {
  roleName: string
  rentalStatus?: string | null
  rentalProgress?: string | null
  isCancelClaim?: boolean
  libertyApproved?: boolean
  allhasCheckNumber?: boolean
}): boolean {
  const roleName = args.roleName
  const rentalStatus = String(args.rentalStatus || '').trim()
  const rentalProgress = String(args.rentalProgress || '').trim()
  const isCancelClaim = Boolean(args.isCancelClaim)
  const isPmc =
    (RESIDENT_COMPANY_ROLES as readonly string[]).includes(roleName) ||
    (RESIDENT_PROPERTY_ROLES as readonly string[]).includes(roleName)

  const roleAllowlist = (CALCULATION_CONTENT_PANEL_ROLES as readonly string[]).includes(
    roleName,
  )
  const pmcApprovedWithCheck =
    isPmc && Boolean(args.libertyApproved) && Boolean(args.allhasCheckNumber)
  const pmcCompleted = isPmc && rentalProgress === 'COMPLETED'
  const notIdleRental = !['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(rentalStatus)
  const cancelClaimBranch =
    ['ACTIVE', 'CLOSED'].includes(rentalStatus) && isCancelClaim

  return (
    ((roleAllowlist || pmcApprovedWithCheck || pmcCompleted) && notIdleRental) ||
    cancelClaimBranch
  )
}

/**
 * Angular `partialPayment()`: when Other Adjustments (loss_adjustment) is ≤ 0 and
 * Partial Payments has a value, copy partial into loss_adjustment.
 */
export function applyPartialPaymentToLossAdjustment(args: {
  lossAdjustment?: string | null
  partialPaymentAmount?: string | null
}): { loss_adjustment?: string } {
  const lossRaw = String(args.lossAdjustment ?? '').replace(/[^0-9.-]/g, '')
  const loss = lossRaw === '' ? 0 : Number(lossRaw)
  const partial = String(args.partialPaymentAmount ?? '').trim()
  if ((!Number.isFinite(loss) || loss <= 0) && partial) {
    return { loss_adjustment: partial }
  }
  return {}
}

/**
 * Returns true when the rental_status option should be disabled in the
 * `<select>` for the given role. Matches Angular's [disabled] expression in
 * the resident-edit-new template.
 */
export function isRentalStatusOptionDisabled(
  statusKey: string,
  roleName: string,
  applicationData?: { contract_start_date?: string | null; lease_start_date?: string | null } | null,
): boolean {
  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(roleName)
  if (!isLibertyRole && (statusKey === 'ACTIVE' || statusKey === 'NOT_STARTED')) {
    const contractDateRaw = applicationData?.contract_start_date || applicationData?.lease_start_date
    const contractDate = contractDateRaw ? parseCalendarDate(contractDateRaw) : null
    const contractStarted = contractDate && !isAfter(startOfDay(contractDate), startOfDay(new Date()))
    
    if (statusKey === 'NOT_STARTED' && contractStarted) return true
    if (statusKey === 'ACTIVE' && !contractStarted) return true
  }
  return false
}

/**
 * Returns true when a rental_progress option should be disabled for the
 * current role and rental_status. Matches the Angular template logic.
 */
export function isRentalProgressOptionDisabled(
  progressKey: string,
  rentalStatus: string,
  roleName: string,
): boolean {
  if (
    progressKey === 'AWAITING_NEW_RESIDENT' ||
    progressKey === 'EXTERNAL_PENDING' ||
    progressKey === 'ACH_CHECK_PENDING'
  ) {
    return true
  }

  const isPropertyOrRegionalRole =
    RESIDENT_PROPERTY_ROLES.includes(roleName) || roleName === 'ROLE_REGIONAL_MANAGER'
  if (isPropertyOrRegionalRole && !['EARLY_END', 'NOT_MOVED_IN'].includes(progressKey)) {
    return true
  }

  if (
    rentalStatus === 'CLOSED' &&
    progressKey === 'NOT_MOVED_IN' &&
    !['ROLE_ADMIN', 'ROLE_LIBERTY_DEFAULTS'].includes(roleName)
  ) {
    return true
  }

  return false
}

/**
 * Determines which detail-page tabs are visible for the role / claim state.
 *  - residentinfo: always
 *  - default (Reimbursement): only when rental_status is SKIPPED/EVICTION
 *  - calculation: only Liberty roles, SKIPPED/EVICTION rental status, NOT
 *    Customer Service / Refund Approval, and DENIED/PAID hides for non-Liberty
 *  - libertyinternal: requires `resident_libertyinternal_view` policy
 *  - policy: when default_policy === 'COMPANY'
 */
export function getVisibleResidentTabs(args: {
  roleName: string
  residentDetail: ResidentDetailResponse | null
  hasPolicy: (policies: string[]) => boolean
}): Array<{
  id: 'residentinfo' | 'default' | 'calculation' | 'libertyinternal' | 'policy' | 'pmsintegration'
  label: string
}> {
  const { roleName, residentDetail, hasPolicy } = args
  const residentDefault = residentDetail?.resident_default
  const rentalStatus = String(residentDefault?.rental_status || '')
    .trim()
    .toUpperCase()
  const claimStatus = String(residentDefault?.claim_status || '')
  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(roleName)
  const isReimbursement = REIMBURSEMENT_RENTAL_STATUSES.has(rentalStatus)

  const tabs: Array<{
    id: 'residentinfo' | 'default' | 'calculation' | 'libertyinternal' | 'policy' | 'pmsintegration'
    label: string
  }> = [
    { id: 'residentinfo', label: 'Resident Info' },
  ]

  if (isReimbursement) {
    tabs.push({ id: 'default', label: 'Reimbursement' })
  }

  const calculationGated =
    isReimbursement &&
    isLibertyRole &&
    !['ROLE_CUSTOMER_SERVICE', 'ROLE_CUSTOMER_SERVICE_MANAGER', 'ROLE_REFUND_APPROVAL'].includes(roleName) &&
    !(['DENIED', 'PAID'].includes(claimStatus) && !isLibertyRole)
  if (calculationGated) {
    tabs.push({ id: 'calculation', label: 'Calculation' })
  }

  if (hasPolicy([RESIDENT_POLICIES.libertyInternalView])) {
    tabs.push({ id: 'libertyinternal', label: 'Liberty Internal' })
  }

  if (residentDetail?.policy?.default_policy === 'COMPANY') {
    tabs.push({ id: 'policy', label: 'Policy' })
  }

  // Angular gates on plain JS truthiness of `is_pms_property` — any truthy value enables the tab.
  const isPmsProperty = Boolean(residentDetail?.is_pms_property)
  if (isPmsProperty && hasPolicy([RESIDENT_POLICIES.yardiConfig])) {
    tabs.push({ id: 'pmsintegration', label: 'PMS Integration' })
  }

  return tabs
}

export function shouldShowSaveBar(selectedTab: string): boolean {
  return SAVE_BUTTON_TABS.has(selectedTab)
}

/** Roles that may cancel reimbursement even when the user is in `RESIDENT_LIBERTY_ROLES`. Mirrors Angular `resident-edit-new` Cancel Reimbursement row. */
const ANGULAR_CANCEL_REIMB_WHITELIST = new Set([
  'ROLE_ADMIN',
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_COMPANY_ADMIN',
  'ROLE_COMPANY_MANAGER',
  'ROLE_REGIONAL_MANAGER',
  'ROLE_PROPERTY_ADMIN',
  'ROLE_PROPERTY_MANAGER',
  'ROLE_PROPERTY_AGENT',
])

/**
 * Angular shows "Cancel Reimbursement" on the Reimbursement tab (not the global footer)
 * when the claim is in a cancellable rental state, progress is not completed, and
 * `(whitelist role || not a Liberty internal role)`.
 */
export function canShowCancelReimbursementButton(
  roleName: string,
  residentDefault?: Pick<ResidentDefault, 'rental_status' | 'rental_progress'>,
): boolean {
  const rs = residentDefault?.rental_status
  const rp = residentDefault?.rental_progress
  if (!rs || ['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(rs)) return false
  if (rp === 'COMPLETED') return false
  const liberty = (RESIDENT_LIBERTY_ROLES as readonly string[]).includes(roleName)
  return ANGULAR_CANCEL_REIMB_WHITELIST.has(roleName) || !liberty
}

/**
 * Angular `resident-edit-new` Reimbursement Review block: Liberty roles excluding
 * Customer Service / CS Manager / External Insurance; rental status not NOT_STARTED / ACTIVE / CLOSED.
 */
export function canShowReimbursementReviewBlock(
  roleName: string,
  rentalStatus?: string | null,
): boolean {
  if (!(RESIDENT_LIBERTY_ROLES as readonly string[]).includes(roleName)) return false
  if (
    ['EXTERNAL_INSURANCE', 'ROLE_CUSTOMER_SERVICE', 'ROLE_CUSTOMER_SERVICE_MANAGER'].includes(roleName)
  ) {
    return false
  }
  const rs = String(rentalStatus || '').trim()
  if (!rs || ['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(rs)) return false
  return true
}

/**
 * Angular Adjust Payment row (~2465): Liberty (not External Insurance) + payment_history[0]
 * has liberty_authorization_user_name or insurance_user_name.
 */
export function canShowAdjustPaymentButton(
  roleName: string,
  paymentHistory?: Array<{
    liberty_authorization_user_name?: string | null
    insurance_user_name?: string | null
  }> | null,
): boolean {
  if (!(RESIDENT_LIBERTY_ROLES as readonly string[]).includes(roleName)) return false
  if (roleName === 'EXTERNAL_INSURANCE') return false
  const first = paymentHistory?.[0]
  if (!first) return false
  return Boolean(first.liberty_authorization_user_name || first.insurance_user_name)
}

/** Sum of signed payment_history amounts excluding recalculated rows — Angular `totalPaid`. */
export function sumPaymentHistoryTotalPaid(
  paymentHistory?: Array<{
    payment_amount?: string | number | null
    review_decision?: string | null
  }> | null,
): number {
  if (!paymentHistory?.length) return 0
  let rawSum = 0
  let hasEligibleRow = false

  paymentHistory.forEach((row) => {
    if (
      row.review_decision === 'L1 Recalculated' ||
      row.review_decision === 'L2 Recalculated'
    ) {
      return
    }
    const rawVal = String(row.payment_amount ?? '0').replace(/[^0-9.-]/g, '')
    const n = parseInt(rawVal, 10)
    if (Number.isFinite(n)) {
      rawSum += n
      hasEligibleRow = true
    }
  })

  return hasEligibleRow ? -rawSum : 0
}

/**
 * Angular `previousDefaultPayment` for Adjust Payment modal (`default-payment.component.ts:127`):
 * Sums Math.abs(payment_amount), but for rows with payment_status === 'Recovered',
 * it negates the amount (payment_amount * -1).
 */
export function sumPaymentHistoryPreviousDefaultPayment(
  paymentHistory?: Array<{
    payment_amount?: string | number | null
    payment_status?: string | null
  }> | null,
): number {
  if (!paymentHistory?.length) return 0
  let total = 0
  paymentHistory.forEach((key) => {
    const rawVal = String(key.payment_amount ?? '0').replace(/[^0-9.-]/g, '')
    const amt = Number(rawVal)
    if (!Number.isFinite(amt)) return
    if (key.payment_status === 'Recovered') {
      total += amt * -1
    } else {
      total += Math.abs(amt)
    }
  })
  return total
}

/**
 * Angular `selectedPayment` edit mode initial payment amount derivation (`default-payment.component.ts:107-122`).
 * If payment is unapproved (payment_status !== 'COMPLETED' && liberty_approved !== '1'),
 * sums approved/completed payments in payment_history and, if totalApprovedPaid < net_due,
 * sets initialAmount to (net_due - totalApprovedPaid).
 */
export function deriveEditPaymentInitialAmount(
  selectedPayment: {
    payment_amount?: string | number | null
    payment_status?: string | null
    liberty_approved?: string | null
  } | null,
  paymentHistory?: Array<{
    payment_amount?: string | number | null
    payment_status?: string | null
    liberty_approved?: string | null
  }> | null,
  netDue?: number | string | null,
): number {
  if (!selectedPayment) return 0
  const rawAmt = Number(String(selectedPayment.payment_amount ?? '0').replace(/[^0-9.-]/g, ''))
  const baseAmount = Number.isFinite(rawAmt) ? Math.abs(rawAmt) : 0

  const isCompleted = selectedPayment.payment_status === 'COMPLETED'
  const isApproved = String(selectedPayment.liberty_approved ?? '') === '1'

  if (isCompleted || isApproved) {
    return baseAmount
  }

  let totalApprovedPaid = 0
  if (paymentHistory?.length) {
    paymentHistory.forEach((row) => {
      if (!row) return
      const rowApproved = String(row.liberty_approved ?? '') === '1'
      const rowCompleted = row.payment_status === 'COMPLETED'
      if (rowApproved || rowCompleted) {
        const amt = Number(String(row.payment_amount ?? '0').replace(/[^0-9.-]/g, ''))
        if (Number.isFinite(amt)) {
          totalApprovedPaid += Math.abs(amt)
        }
      }
    })
  }

  const net = Number(String(netDue ?? '0').replace(/[^0-9.-]/g, '')) || 0
  if (totalApprovedPaid < net) {
    return net - totalApprovedPaid
  }

  return baseAmount
}

export function monthDifference(start: Date, end: Date): number {
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
}

export function getFirstMonthNotPaidDate(month?: string, year?: string): Date | null {
  if (!month || !year) return null
  const parsedMonth = Number(month)
  const parsedYear = Number(year)
  if (!Number.isFinite(parsedMonth) || !Number.isFinite(parsedYear)) return null
  return new Date(parsedYear, parsedMonth - 1, 1)
}

export function canShowAccessConfirmation(balanceDue?: number | string | null): boolean {
  if (balanceDue === undefined || balanceDue === null || balanceDue === '') return true
  return Number(balanceDue) <= 0
}

/**
 * Angular resident detail checks `progress_status === 'CERTIFICATE_SENT'`;
 * application detail / API display labels use `'Access Sent'`. Accept both
 * (case-insensitive for the enum key).
 */
export function isAccessSentProgressStatus(progressStatus: unknown): boolean {
  const value = String(progressStatus ?? '').trim()
  if (!value) return false
  const upper = value.toUpperCase().replace(/\s+/g, '_')
  return (
    value === 'CERTIFICATE_SENT' ||
    value === 'Access Sent' ||
    upper === 'CERTIFICATE_SENT' ||
    upper === 'ACCESS_SENT'
  )
}

/**
 * Angular resident-edit-new.component.html Download Access Confirmation gate:
 * progress_status Access Sent/CERTIFICATE_SENT + APPROVED + rent_added + !balance_due.
 * `balance_due` uses numeric comparison so `"0"` / `"0.00"` still allow the button.
 * `rent_added` follows Angular truthiness (true/1/"1"/non-empty), not strict boolean only.
 */
export function canShowResidentDownloadAccessConfirmation(applicationData?: {
  progress_status?: unknown
  application_status?: unknown
  rent_added?: unknown
  balance_due?: number | string | null
} | null): boolean {
  if (!applicationData) return false
  const rentAdded = applicationData.rent_added
  const hasRentAdded =
    rentAdded === true ||
    rentAdded === 1 ||
    rentAdded === '1' ||
    rentAdded === 'true' ||
    (typeof rentAdded === 'string' && rentAdded.trim() !== '' && rentAdded !== '0' && rentAdded.toLowerCase() !== 'false')
  const status = String(applicationData.application_status ?? '').trim().toUpperCase()
  return (
    isAccessSentProgressStatus(applicationData.progress_status) &&
    status === 'APPROVED' &&
    hasRentAdded &&
    canShowAccessConfirmation(applicationData.balance_due)
  )
}

/**
 * Liberty roles can view application approver / lease end / payment status
 * blocks. Mirrors `liberty_roleslist.includes(...)` checks in the template.
 */
export function isLibertyRole(roleName: string): boolean {
  return RESIDENT_LIBERTY_ROLES.includes(roleName)
}

/**
 * PMC = company + property roles. Used in the Angular template under the
 * alias `companyproperty_roleslist`.
 */
export function isCompanyOrPropertyRole(roleName: string): boolean {
  return RESIDENT_COMPANY_ROLES.includes(roleName) || RESIDENT_PROPERTY_ROLES.includes(roleName)
}

/**
 * Mirrors Angular's `rentalStatusChange` auto-progress logic. When the
 * rental_status changes, the rental_progress field is auto-set to the first
 * available option for the new status. Property roles + RegionalManager have
 * a different fallback: NOT_STARTED -> NOT_MOVED_IN, CLOSED -> EARLY_END.
 */
export function getInitialRentalProgress(
  rentalStatus: string,
  roleName: string,
  rentalList: RentalListResponse | null,
): string {
  if (!rentalStatus || !rentalList?.rental_status?.[rentalStatus]?.progress) return ''
  const progressMap = rentalList.rental_status[rentalStatus].progress as Record<string, unknown>
  const keys = Object.keys(progressMap)
  if (!keys.length) return ''
  let firstValue = keys[0]

  const isPropertyOrRegional =
    RESIDENT_PROPERTY_ROLES.includes(roleName) || roleName === 'ROLE_REGIONAL_MANAGER'
  if (isPropertyOrRegional) {
    if (rentalStatus === 'NOT_STARTED') firstValue = 'NOT_MOVED_IN'
    else if (rentalStatus === 'CLOSED') firstValue = 'EARLY_END'
  }
  return firstValue
}

/**
 * Mirrors Angular's `validateFirstMonth`. Sets `timely` to YES when the
 * first-month-not-paid month/year matches the rental_status_date month/year,
 * NO otherwise. Also derives `adjusted_notice` and `claim_expiration_date`.
 *
 * Non-liberty year rollback (Angular ~2235–2246): when first month is after the
 * request month, roll year −1 if that month sits between lease start and request.
 */
export function deriveTimelyAndDates(values: {
  rental_status_date?: string
  first_month_not_paid_month?: string
  first_month_not_paid_year?: string
  lease_start_date?: string
  isLibertyRole?: boolean
}): {
  timely?: 'YES' | 'NO'
  adjusted_notice?: string
  claim_expiration_date?: string
  /** Present when non-liberty year rollback adjusted the year. */
  first_month_not_paid_year?: string
} {
  const { rental_status_date, first_month_not_paid_month, first_month_not_paid_year } = values
  if (!rental_status_date || !first_month_not_paid_month || !first_month_not_paid_year) return {}

  const requestDate = parseCalendarDateLocal(rental_status_date)
  if (!requestDate) return {}
  const requestYear = requestDate.getFullYear()
  const requestMonth = requestDate.getMonth() + 1
  const firstMonth = Number(first_month_not_paid_month)
  let firstYear = Number(first_month_not_paid_year)
  if (!Number.isFinite(firstMonth) || !Number.isFinite(firstYear)) return {}

  let rolledYear: string | undefined
  if (!values.isLibertyRole) {
    const firstMonthStart = new Date(firstYear, firstMonth - 1, 1)
    const requestMonthStart = new Date(requestYear, requestMonth - 1, 1)
    if (firstMonthStart.getTime() > requestMonthStart.getTime()) {
      const priorYear = firstYear - 1
      const priorMonthStart = new Date(priorYear, firstMonth - 1, 1)
      const leaseStart = values.lease_start_date
        ? parseCalendarDateLocal(values.lease_start_date)
        : null
      const leaseMonthStart = leaseStart
        ? new Date(leaseStart.getFullYear(), leaseStart.getMonth(), 1)
        : null
      if (
        leaseMonthStart &&
        priorMonthStart.getTime() > leaseMonthStart.getTime() &&
        priorMonthStart.getTime() < requestMonthStart.getTime()
      ) {
        firstYear = priorYear
        rolledYear = String(priorYear)
      }
    }
  }

  const isTimely = requestYear === firstYear && requestMonth === firstMonth
  if (isTimely) {
    const startOfMonth = new Date(requestYear, requestMonth - 1, 1)
    const expiration = new Date(requestYear, requestMonth - 1 + 6, 0)
    return {
      timely: 'YES',
      adjusted_notice: toIsoDate(startOfMonth),
      claim_expiration_date: toIsoDate(expiration),
      ...(rolledYear ? { first_month_not_paid_year: rolledYear } : {}),
    }
  }

  const expirationDate = new Date(firstYear, firstMonth - 1 + 6, 0)
  return {
    timely: 'NO',
    adjusted_notice: toIsoDate(requestDate),
    claim_expiration_date: toIsoDate(expirationDate),
    ...(rolledYear ? { first_month_not_paid_year: rolledYear } : {}),
  }
}

/**
 * Angular `setResidentData` keeps a saved Reimbursement Start Date
 * (`adjusted_notice`) and only runs `validateFirstMonth` when that field is
 * empty or when request date / first-month inputs change.
 *
 * React must not re-apply system-derived Start Date over a saved or manually
 * adjusted value on hydrate — that was CS-1148 (adjusted dates reverting).
 */
export function shouldSyncDerivedAdjustedNotice(options: {
  requestOrFirstMonthDirty: boolean
  currentAdjustedNotice?: string | null
}): boolean {
  if (options.requestOrFirstMonthDirty) return true
  return !String(options.currentAdjustedNotice ?? '').trim()
}

/** Local calendar parse (noon) so month/year math matches Angular moment day units. */
function parseCalendarDateLocal(value: string): Date | null {
  const s = String(value ?? '').trim()
  if (!s) return null
  const iso = /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(0, 10)}T12:00:00` : s
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Mirrors Angular's `updateContract`. When the lease_start_date changes, the
 * lease_end_date defaults to start + 1 year - 1 day, and `lease_verified` is
 * set to '1'.
 */
export function deriveLeaseEndFromStart(leaseStartDate: string): {
  lease_end_date?: string
  lease_verified?: string
} {
  if (!leaseStartDate) return {}
  // Use calendar-local parse (not `new Date('YYYY-MM-DD')` UTC) so end date matches Angular moment().
  const start = parseCalendarDateLocal(leaseStartDate)
  if (!start) return {}
  const end = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1)
  return { lease_end_date: toIsoDate(end), lease_verified: '1' }
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Angular `(!oldApplication || rental_progress !== 'COMPLETED' || (isNewPayment && rental_progress === 'COMPLETED'))`
 * Gates: Reimbursement Start Date, Adjusted Reason, and Financial Summary/Adjustments/Totals blocks.
 */
export function canShowCalculationFinancials({
  residentDetail,
  rentalProgress,
}: {
  residentDetail: ResidentDetailResponse | null | undefined
  rentalProgress?: string
}): boolean {
  const rd = residentDetail?.resident_default
  const progress = rentalProgress || rd?.rental_progress

  let oldApplication = false
  if (rd?.default_status_date) {
    const dt = Date.parse(String(rd.default_status_date))
    if (!Number.isNaN(dt) && dt < Date.now()) {
      oldApplication = true
    }
  }

  if (!oldApplication) return true
  if (progress !== 'COMPLETED') return true

  let isNewPayment = false
  const JUNE_1_2020_MS = new Date('2020-06-01T00:00:00Z').getTime()

  const checkPaymentRow = (row: Record<string, unknown> | null | undefined) => {
    if (!row || row.created_at === undefined || row.created_at === null || row.created_at === '') return
    const val = row.created_at
    let ms: number
    if (typeof val === 'number') {
      ms = val < 1e12 ? val * 1000 : val
    } else {
      ms = Date.parse(String(val))
    }
    if (!Number.isNaN(ms) && ms > JUNE_1_2020_MS) {
      isNewPayment = true
    }
  }

  for (const row of (residentDetail?.payment_review_log || []) as Array<Record<string, unknown>>) {
    checkPaymentRow(row)
  }
  for (const row of (residentDetail?.payment_history || []) as Array<Record<string, unknown>>) {
    checkPaymentRow(row)
  }

  return isNewPayment
}
